import { useFlags } from 'launchdarkly-react-client-sdk';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import {
  fetchPreviousCalls,
  markPreviousCallReportingEnabled,
  previousCallToBeReportedSelected,
  selectPreviousCalls,
} from 'features/call/vrs-call-comments/store';
import { userSelector } from 'features/user/store';

export function usePreviousCalls() {
  const dispatch = useAppDispatch();
  const previousCalls = useAppSelector(selectPreviousCalls);
  const userId = useAppSelector(userSelector).id;
  const { previousCallReportingEnabled } = useFlags();

  // Track if component is still mounted to prevent race conditions
  const isMountedRef = useRef(true);

  // Local selection (callId from UI list). Start undefined; we'll set it once data arrives.
  const [selectedCallId, setSelectedCallId] = useState<number | undefined>(
    undefined
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch once we have a user ID.
  useEffect(() => {
    if (userId && previousCallReportingEnabled) {
      dispatch(fetchPreviousCalls());
      dispatch(markPreviousCallReportingEnabled());
    }
  }, [dispatch, previousCallReportingEnabled, userId]);

  // Keep selection valid when data changes:
  // - If nothing selected or current selection disappeared, select the first item.
  // - If list becomes empty, clear selection.
  useEffect(() => {
    if (!isMountedRef.current) {
      return;
    }

    if (previousCalls.length === 0) {
      if (selectedCallId !== undefined) {
        setSelectedCallId(undefined);
      }
      return;
    }

    // edge case in case it lapses over 24 hours.
    const stillExists = previousCalls.some((c) => c.callId === selectedCallId);
    if (selectedCallId === undefined || !stillExists) {
      const first = previousCalls[0];
      setSelectedCallId(first.callId);
      dispatch(previousCallToBeReportedSelected(first.callId));
    }
  }, [previousCalls, selectedCallId, dispatch]);

  // Debounced analytics to prevent rapid-fire events
  const debouncedAnalytics = useMemo(
    () =>
      debounce((callId: number) => {
        dispatch(
          sendAnalyticsInfo({
            callId,
            event: 'previous_call_reported_call_selected',
            Method: 'selectPreviousCallFromDropdown',
          })
        );
      }, 300),
    [dispatch]
  );

  // User selects a call from the UI.
  const selectCall = useCallback(
    (id: string | number) => {
      const callId = typeof id === 'string' ? Number(id) : id;
      if (Number.isNaN(callId) || callId === selectedCallId) {
        return;
      }
      const selected = previousCalls.find((c) => c.callId === callId);
      if (!selected) {
        console.warn('Attempted to select non-existent call:', id);
        return;
      }

      setSelectedCallId(callId);
      dispatch(previousCallToBeReportedSelected(selected.callId));
      debouncedAnalytics(selected.callId);
    },
    [previousCalls, selectedCallId, dispatch, debouncedAnalytics]
  );

  // Derive the selected object without extra state.
  const selectedCall = useMemo(
    () => previousCalls.find((c) => c.callId === selectedCallId) || null,
    [previousCalls, selectedCallId]
  );

  return {
    previousCalls,
    selectedCallId,
    selectedCall,
    selectCall,
    isEmpty: previousCalls.length === 0,
  };
}
